import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Stack, styled, Typography } from '@mui/material'
import type { ButtonProps } from '@mui/material'
import type { FC, ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import type { HoldingWeekDto } from '@hcr/api/consumer'
import { COLORS, ICONS, LinkUnstyled, remUnit, unit } from '@hcr/ui'

import { DateFormat, Path } from '../../../../../../../models'
import { dateFormatter, to } from '../../../../../../../utils'

interface WeekPreviewProps {
  holdingId: string
  week: HoldingWeekDto
}

export const WeekPreview: FC<WeekPreviewProps> = ({ holdingId, week }) => {
  const { t } = useTranslation()

  const variant = week.is_locked ? 'text' : 'outlined'
  const actionLabel =
    week.is_locked || week.is_rental_request_received ? t('owner-holding.rented-out') : t('owner-holding.rent-out')
  const weekLabel = t('owner-holding.week', { weekNumber: week.week_number })
  const dateRangeLabel = dateFormatter.formatDestinationDateRange({
    from: week.week_start,
    fromFormat: DateFormat.Basic,
    to: week.week_end,
    toFormat: DateFormat.Basic,
  })

  const actionAriaLabel = [
    `${weekLabel}, ${dateRangeLabel}`,
    actionLabel,
    week.is_locked && t('owner-holding.not-available'),
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <Stack
      direction='column'
      justifyContent='space-between'
      alignItems='start'
      flexWrap='wrap'
      paddingRight={unit(6)}
      paddingBottom={unit(5)}
      paddingLeft={unit(3.25)}
      bgcolor={COLORS.white}
    >
      <Typography variant='labelM'>
        {weekLabel}, {dateRangeLabel}
      </Typography>
      {week.is_rental_request_received ? (
        <Stack direction='row' justifyContent='center' alignItems='center' gap={unit(2)} marginTop={unit(2)}>
          <FontAwesomeIcon color={COLORS.semantic.success} fontSize={remUnit(4)} icon={ICONS.farCheckCircle} />
          <Typography variant='labelM' color={COLORS.semantic.success}>
            {actionLabel}
          </Typography>
        </Stack>
      ) : (
        <ActionButton
          component={LinkUnstyled}
          to={to([Path.OwnerHoldingRental, { holdingId, weekStart: week.week_start }])}
          onClick={week.is_locked ? event => event.preventDefault() : undefined}
          aria-label={actionAriaLabel}
          endIcon={
            week.is_locked && (
              <FontAwesomeIcon icon={ICONS.farLockKeyhole} style={{ fontSize: remUnit(4) }} aria-hidden='true' />
            )
          }
          disabled={week.is_locked}
          variant={variant}
          color='black'
        >
          {actionLabel}
        </ActionButton>
      )}
    </Stack>
  )
}

const ActionButton = styled(Button)`
  text-align: right;
  margin-top: ${unit(3.5)};
` as (props: ButtonProps<typeof LinkUnstyled>) => ReactElement
